import { Button, Cell, PreviewCard } from '@zeiss/beyond-online-react';
import { Box, ButtonGroup, Grid } from '@zeiss/lib-components';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_DATE_TIME_FORMAT, INVITATION_EXPIRATION } from 'src/constants';
import {
  useInvitationClipboard,
  useSendInvitation,
} from 'src/modules/Invitations/hooks';
import { useWizardForm } from 'src/modules/Invitations/InvitationCreate/form';
import type { StepComponent } from 'src/modules/Invitations/InvitationCreate/types';
import { getInvitationDetailPath } from 'src/modules/Invitations/utils';
import { InvitationType } from 'src/types/rest/managementPortalApi';

export const StepSendFields: StepComponent = ({ createdInvitation }) => {
  const { t } = useTranslation(['common', 'invitations']);
  const navigate = useNavigate();
  const { copyInvitationLink, textCopied } = useInvitationClipboard();
  const [createdAt, setCreatedAt] = useState<number | null>(null);

  useEffect(() => {
    setCreatedAt(Date.now());
  }, []);

  const { isPending, sendInvitation } = useSendInvitation();

  const form = useWizardForm();

  const invitationType = form.getFieldValue('step1.invitationType');
  const invitationFor = form.getFieldValue('step1.invitationFor');
  const email = form.getFieldValue('step2.email');

  if (!createdInvitation || !invitationType) {
    return null;
  }

  const validUntil = format(
    new Date((createdAt ?? Date.now()) + INVITATION_EXPIRATION),
    DEFAULT_DATE_TIME_FORMAT,
  );

  const onViewInvitation = () => {
    const searchParam =
      invitationType === InvitationType.application
        ? { applicationId: invitationFor?.id }
        : { tenantId: invitationFor?.id };

    const path = getInvitationDetailPath({
      code: createdInvitation.code,
      ...searchParam,
    });

    navigate(`../${path}`);
  };

  return (
    <Grid>
      <Cell.Basic colSpan={[4, 6, 8]}>
        <Box>
          <PreviewCard
            chips={[]}
            eyebrow={t('invitations:inviteCode')}
            headline={createdInvitation.code}
            link={[
              {
                icon: 'ArrowRight',
                label: t('invitations:action.viewInvitation'),
                onClick: onViewInvitation,
              },
            ]}
            properties={[
              {
                key: t('common:glossary.email'),
                value: email,
              },
              {
                key: t('invitations:invitedTo'),
                value: invitationFor?.label ?? '',
              },
              {
                key: t('invitations:validUntil'),
                value: validUntil,
              },
            ]}
          />
        </Box>

        <Box>
          <ButtonGroup spacing="s" variant="form">
            <Button
              icon={textCopied ? 'Checkmark' : 'CopyContent'}
              onClick={() =>
                copyInvitationLink(createdInvitation.invitationUrl)
              }
              size="l"
              variant="secondary"
            >
              {t('invitations:action.copyInvitationLink')}
            </Button>

            <Button
              disabled={isPending}
              icon="SendMessage"
              loading={isPending}
              onClick={() => sendInvitation(createdInvitation.code)}
              size="l"
            >
              {t('invitations:action.sendInvitationByEmail')}
            </Button>
          </ButtonGroup>
        </Box>
      </Cell.Basic>
    </Grid>
  );
};
